import * as React from 'react';
import { yupResolver } from '@hookform/resolvers';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import { addBusinessDays, format, parseISO } from 'date-fns';
import { useLazyGetInspectionFollowUpDateQuery } from '../../redux/rtkQuery/apiSlice';
import * as yup from 'yup';
import { DeleteOutline, Warning as WarningIcon } from '@material-ui/icons';
import { Button } from '@material-ui/core';
import ReactInputMask from 'react-input-mask';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ButtonWithLoader,
  CheckboxWithLabel,
  InputBaseContainer,
  InputDatePickerWithLabel,
  InputSelectWithLabel,
  InputTextWithLabel,
  ModalDefaultDraggable,
  TextAreaWithLabel,
} from '../../molecules';

import CommentsChat from '../../organisms/CommentsChat';

import { FormServerError } from '../../types';
import {
  COMMENTS_DATETIME_FORMAT,
  DEFAULT_DATE_FORMAT,
  GET_ALL_QUERY_PARAMS,
  InspectionStatuses,
} from '../../constants';
import { REPORT_REQUIRED_STATUSES } from '../../pages/Reports/DailyInspectionReport/InspectionEditor/constants';

import { formatDateTime } from '../../utils/formatDate';

import { getCurrentUser } from '../../api/userSettings';
import {
  createPermitInspectionsComment,
  getPermitInspectionById,
  getPermitInspectionsComments,
  sendInspectionMessage,
  getTradesByProjectType,
} from '../../api/inspections';
import isYupFieldRequired from '../../utils/isYupFieldRequired';
import { getEffectiveTradeIds } from '../../utils/inspectorTradeQualification';
import {
  getActivitiesList,
  getActivitiesStatusList,
  getCustomActivities,
} from '../../api/activities';
import {
  deleteInspectionReport,
  downloadInspectionReport,
  getInspectionEditTypes,
  getInspectionTypeByID,
  getInspectionTypes,
  getInspectionTypesCustom,
  uploadInspectionReport,
} from '../../api/inspectionTypes';
import { getTrades } from '../../api/trades';
import { getPermitUsers, getUserByID, getUsers, getUsersCustom } from '../../api/users';
import parseUTCDate from '../../utils/parseUTCDate';
import { USER_TYPES_VALUES, CLIENT_TYPES_VALUES } from '../../constants/forms';
import { getPermitByID } from '../../api/permits';
import { getProjectByID, getProjects } from '../../api/projects';
import InspectionsInfoBlock from '../../organisms/InspectionsInfoBlock';
import PermitViewContacts from '../../organisms/PermitViewContacts';
import setInspectionData from '../../utils/setInspectionData';
import useUserRole from '../../hooks/useUserRole';
import ModalInfo from '../../molecules/ModalInfo';
import useDevice from '../../hooks/useDevice';
import DesktopAddReport from './DesktopAddReport';
import { RootState } from '../../redux/root';
import { setModalOpen, setPermitInspectionsList, setShowSuccessAndPrint } from '../../redux/slices/inspectionsSlice';
import { InspectorSet } from '../../utils/InspectorSetTools';
import StatusChangeConfirmPopup from './StatusChangeConfirmPopup';
import { setPromptMessage, setShowFormPopup } from '../../redux/slices/InspectionsReportSlice';

const labels = {
  activityTitle: 'Activity Title',
  inspectionType: 'Inspection Type',
  information: 'Information',
  trades: 'Trades',
  activityContact: 'Activity Contact',
  inspectionDate: 'Inspection Date',
  activityStatus: 'Activity Status',
  period: 'AM/PM',
  inspector: 'Inspector',
  avOperator: 'A/V Operator',
  clientOnSite: 'Client on Site',
  followUp: 'Follow-up date',
  assignedTo: 'Assigned to',
  emergency: 'Emergency',
  isCompleted: 'Completed',
  activityContactCheckbox: 'Activity Contact',
  jurisdictionContacts: 'Jurisdiction',
  additionalContacts: 'More Contacts',
  cellPhone: 'Cell Phone',
  phoneNumber: 'Phone Number',
  dateCreated: "Date Created",
  createdBy: "Created By",
  dateCompleted: "Date Completed",
  completedBy: "Completed By",
  requestedatf: "Requested After The Fact?",
};

const periodData = [
  { name: <p />, id: 2 },
  { name: 'AM', id: 0 },
  { name: 'PM', id: 1 },
];

const schemaAdd = yup.object().shape({
  activityTitle: yup
    .object()
    .required()
    .typeError('Select a Activity Title from the list')
    .label(labels.activityTitle),
  inspectionType: yup
    .array()
    .of(yup.object())
    .required()
    .typeError('Select a Activity Type from the list')
    .label(labels.inspectionType),
  information: yup
    .string()
    .nullable()
    .label(labels.information),
  cellPhone: yup
    .string()
    .test('len', 'Invalid Cell Number', (val => val.split(" ").join("").length === 12 || val.split(" ").join("").length === 0))
    // .required()
    .nullable()
    .label(labels.cellPhone),
  phoneNumber: yup
    .string()
    .test('len', 'Invalid Phone Number', (val => val.split(" ").join("").length === 12 || val.split(" ").join("").length === 0))
    // .required()
    .nullable()
    .label(labels.phoneNumber),
  activityContact: yup
    .object()
    .shape({
      value: yup.string(),
      label: yup.string(),
    })
    .nullable()
    // .required()
    // .typeError('Select a Activity Contact from the list')
    .label(labels.activityContact)
    .when(["cellPhone", "phoneNumber"], (cellPhone, phoneNumber, schema) => {
      if (cellPhone.length || phoneNumber.length)
        { return schema.required("Must Select Activity Contact"); }
      return schema;
    }),
  emergency: yup.boolean().label(labels.emergency),
  requestedatf: yup.boolean().label(labels.requestedatf),
  clientonsite: yup.boolean().label(labels.clientonsite),
});

const statusesRequiringPhone = [
  InspectionStatuses.SCHEDULED,
  InspectionStatuses.READY_FOR_SCHEDULING,
];

const schemaEdit = yup.object().shape({
  activityTitle: yup
    .object()
    .shape({
      value: yup.string(),
      label: yup.string(),
    })
    .required()
    .typeError('Select a Activity Title from the list')
    .label(labels.activityTitle),
  inspectionType: yup
    .mixed()
    .notOneOf(
      ['', null, undefined, [], {}],
      'Inspection Type is a required field',
    )
    .required()
    .typeError('Select a Activity Type from the list')
    .label(labels.inspectionType),
  activityStatus: yup
    .object()
    .shape({
      value: yup.string(),
      label: yup.string(),
    })
    .required()
    .typeError('Select a Activity Status from the list')
    .label(labels.activityStatus),
  // inspectionDate: yup.date().label(labels.inspectionDate),
  inspectionDate: yup.mixed().notOneOf([[], {}]),
  period: yup.object().nullable().label(labels.period),
  inspector: yup
    .object()
    .nullable()
    .label(labels.inspector),
  avOperator: yup
    .object()
    .nullable()
    .label(labels.avOperator),
  activityContact: yup
    .object()
    .nullable()
    .label(labels.activityContact),
  information: yup
    .string()
    .nullable()
    .label(labels.information),
  cellPhone: yup
      .string()
      .nullable()
      .label(labels.cellPhone),
  phoneNumber: yup
      .string()
      .nullable()
      .label(labels.phoneNumber),

  // mritunjoy
  // EA-56
  // Cell Phone number field in the inspection form
  // ############
  followUp: yup.mixed().notOneOf([[], {}]),
  assignedTo: yup
    .object()
    .nullable()
    .label(labels.assignedTo),

  emergency: yup.boolean().label(labels.emergency),
  requestedatf: yup.boolean().label(labels.requestedatf),
  clientonsite: yup.boolean().label(labels.clientonsite),
  isCompleted: yup.boolean().label(labels.isCompleted),
  activityContactCheckbox: yup
    .boolean()
    .label(labels.activityContactCheckbox),
  jurisdictionContacts: yup
    .boolean()
    .label(labels.jurisdictionContacts),
  additionalContacts: yup
    .array()
    // .when('isCompleted', {
    //   is: isCompleted => isCompleted,
    //   then: yup.array().of(yup.object()).required(),
    // })
    .nullable()
    .label(labels.additionalContacts),
  hiddenCheck: yup
    .boolean()
    .oneOf([true], 'required')
    .required(),
});

const PermitInspectionsForm = ({
  onSubmit,
  initialValues,
  modalError,
  addButtonText = 'Add new Inspection',
  updateButtonText = 'Update the Inspection',
  initialErrors,
  permit,
  permitStatus,
  allActivities,
  isReadOnly,
  isSimulateDeleted,
  setInitialValues,
  updateTable,
  cameFromSearch,
  projectId,
}: Props) => {
  const dispatch = useDispatch();
  const dailyInspections = useSelector((s:RootState) => s.inspections.permitInspectionsList);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modalSuccess, setModalSuccess] = React.useState(false);
  const selectedPermit = useSelector((s:RootState) => s.projectsSlice.selectedPermit);

  const toDateUTC =
    // eslint-disable-next-line no-nested-ternary
    initialValues?.inspectionDate ? new Date(initialValues?.inspectionDate) : '';

  const followUpUTC =
    // eslint-disable-next-line no-nested-ternary
    initialValues?.followUp ? new Date(initialValues?.followUp) : '';

  // UseState items for InputSelect
  const [titlesData, setTitlesData] = React.useState([]);
  const [statusData, setStatusData] = React.useState([]);

  const [
    inspectionsTypeData,
    setInspectionTypeData,
  ] = React.useState([]);

  const [
    inspectionTypesLoading,
    setInspectionTypesLoading,
  ] = React.useState(false);

  const [
    inspectionTypesLoaded,
    setInspectionTypesLoaded,
  ] = React.useState(false);

  const [tradesData, setTradesData] = React.useState([]);

  const [usersData, setUsersData] = React.useState([]);

  const [
    inspectorsData,
    setInspectorsData,
  ] = React.useState([]);

  const [
    avOperatorData,
    setAvOperatorData,
  ] = React.useState([]);

  const [assignedData, setAssignedData] = React.useState(
    [],
  );

  const [
    additionalContactsData,
    setAdditionalContactsData,
  ] = React.useState([]);

  const [isSubmitting, setIsSubmitting] = React.useState<
    boolean
  >(false);
  const [phoneErrors, setPhoneErrors] = React.useState<{ cellPhone?: string; phoneNumber?: string }>({});
  const [showCompletedWarning, setShowCompletedWarning] = React.useState(false);
  const [selectedStatusValue, setSelectedStatus] = useState<string>('');
  // /useState items for InputSelect

  const [
    inspectionTrades,
    setInspectionTrades,
  ] = React.useState(null);

  const [
    checkboxesError,
    setCheckboxesError,
  ] = React.useState('');

  const [readOnlyInfo, setReadOnlyInfo] = React.useState(
    [],
  );

  const [
    isViewContactsModal,
    setIsViewContactsModal,
  ] = React.useState(false);

  const [
    currentProject,
    setCurrentProject,
  ] = React.useState(null);
  const [modalErr, setModalErr] = React.useState(false);
  const [holdSubmit, setHoldSubmit] = React.useState(false);
  const [conditionHoldSubmit, setConditionHoldSubmit] = React.useState(false);
  const [holdModalOpen, setHoldModalOpen] = React.useState(false);
  const [permitData, setPermitData] = React.useState(null);
  const [conditionHoldModalOpen, setConditionHoldModalOpen] = React.useState(null);
  const showFormPopup = useSelector((s:RootState) => s.inspectionReports.showFormPopup);
  const isDeleted =
    initialValues?.isDeleted || isSimulateDeleted;

  const {
    handleSubmit,
    errors,
    register,
    control,
    formState,
    watch,
    setValue,
    getValues,
  } = useForm({
    resolver: yupResolver(
      initialValues ? schemaEdit : schemaAdd,
    ),
    defaultValues: initialValues
      ? {
          ...initialValues,
          toDate: toDateUTC,
          followUp: followUpUTC,
        }
      : {},
  });

  const [isIpad] = useDevice();
  const selectedStatus = watch('activityStatus');
  const selectedType = watch('inspectionType');
  const selectedTitle = watch('activityTitle');
  const isInspectionCompleted = watch('isCompleted');
  const newInformation = watch('information');
  const inspectionTypeData = watch('inspectionType');
  const clientOnSiteChecked = watch('clientOnSite');
  const watchedAvOperator = watch('avOperator');
  const watchedActivityContact = watch('activityContact');
  // Red-flag the contact field while it still points at the deactivated
  // contact. Clears as soon as the user picks a different (active) one.
  const isContactInactive =
    watchedActivityContact?.isActive === false ||
    (initialValues?.contactIsActive === false &&
      (!watchedActivityContact ||
        String(watchedActivityContact?.id ?? watchedActivityContact) ===
          String(initialValues?.contactId ?? initialValues?.contact)));
  let activityContactCheckbox = '';
  let jurisdictionCheckbox = '';
  const additionalContacts = watch('additionalContacts') || [];
  const [projectTypeTrades, setProjectTypeTrades] = React.useState([]);

  useEffect(() => {
    if (selectedStatus) {
      // NOTE: leaving Scheduled used to wipe the inspector here. The status is now
      // derived from the fields rather than the other way around, so clearing it would
      // fight that: removing only the date flips the status to Not Scheduled, and the
      // inspector — which the user did not touch — must survive that.
      if (initialValues?.statusName === 'Not Scheduled' && selectedStatus.name === 'Scheduled') {
        // `selectedPermit` is only populated when the form is reached through the permit
        // page. Bulk Schedule / All Activities / Search / Inspector open it with the slice
        // still at its `{}` initial state, so `status` may be missing entirely — and when it
        // is present it is sometimes an option object and sometimes a raw label/id.
        const _permitStatus: any = selectedPermit?.status;
        const permitStatusLabel =
          _permitStatus && typeof _permitStatus === 'object'
            ? _permitStatus.label
            : _permitStatus;
        if (permitStatusLabel === 'Hold') {
           dispatch(setPromptMessage('Permit Status is HOLD.'));
           dispatch(setShowFormPopup(true));
        } else if (isConditionOnHold) {
          dispatch(setPromptMessage('There is a condition with either an Inspection Hold or Permit Hold.'));
          dispatch(setShowFormPopup(true));
        }
      }
    }
  }, [selectedStatus]);

  useEffect(() => {
    if (clientOnSiteChecked) {
      setValue('avOperator', null);
    }
  }, [clientOnSiteChecked]);

  useEffect(() => {
    let _selectedType = selectedType;
    if (typeof selectedType === 'object' && selectedType !== null) {
      _selectedType = selectedType.name;
    }
  }, [selectedType]);
  const undoStatusChange = () => {
    const status = {
      activity: 11,
      id: 27,
      isActiveStatus: "True",
      isDeleted: false,
      name: InspectionStatuses.NOT_SCHEDULED,
    };
    setValue('activityStatus', status);
    setValue('inspector', null);
    setValue('toDate', null);
  };
  const IsInspectionTypeDisabled = ():boolean => {
    if (inspectionsTypeData.length === 0) {
      return true;
    }
    if (selectedStatus) {
      return !(selectedStatus.name === InspectionStatuses.SCHEDULED || selectedStatus.name === InspectionStatuses.NOT_SCHEDULED);
    }
    return isLoading ||
           isReadOnly ||
           initialValues?.isCompleted ||
           cameFromSearch ||
           !inspectionsTypeData.length;
 };
  if (!isIpad) {
    activityContactCheckbox = watch(
      'activityContactCheckbox',
    );
    jurisdictionCheckbox = watch(
      'jurisdictionContacts',
    );
  }

  const toDateValue = watch('toDate');
  const [errorReport, setErrorReport] = React.useState("");
  const [commentCreated, setCommentCreated] = React.useState(null);
  const [activeFormTab, setActiveFormTab] = React.useState<'details' | 'comments'>('details');
  const [isConditionOnHold, setIsConditionOnHold] = React.useState(false);
  const [inspectionPrompt, setInspectionPrompt] = React.useState(false);
  const [conditionPrompt, setConditionPrompt] = React.useState(false);

  const setPromptValue = (inspectionHold, conditionHold) => {
    setInspectionPrompt(inspectionHold);
    setConditionPrompt(conditionHold);
  };
  const [fetchFollowUpDate] = useLazyGetInspectionFollowUpDateQuery();
  React.useEffect(() => {
    if (toDateValue) {
      // Follow-up is 2 business days out, so a Thursday inspection follows up on
      // Monday rather than landing on the weekend. Show that right away, then let the
      // backend push it past the franchise's Scheduling holidays too.
      const inspectionDate = new Date(toDateValue);
      setValue('followUp', addBusinessDays(inspectionDate, 2));
      let stale = false;
      fetchFollowUpDate({ inspection_date: format(inspectionDate, 'yyyy-MM-dd'), permit })
        .unwrap()
        .then(result => {
          if (!stale && result?.followUp) {
            setValue('followUp', parseISO(result.followUp));
          }
        })
        .catch(() => {});
      return () => {
        stale = true;
      };
    }
    // The follow-up date only exists relative to an inspection date, so removing the
    // date clears it too instead of leaving a follow-up pointing at nothing.
    setValue('followUp', null);
    return undefined;
  }, [toDateValue]);

  // The status follows the field values, not the other way around: an inspection is
  // Scheduled once it has BOTH a date and an inspector, and Not Scheduled otherwise
  // (an inspector with no date stays Not Scheduled). The server derives the same way
  // on save; mirroring it here shows the user the resulting status immediately and
  // puts the change in the payload so it lands in the audit comments. Statuses
  // outside the scheduling lifecycle (Pass, Failed, Cancelled, …) are left alone.
  const inspectorValue = watch('inspector');

  // EA-1195: Determine which inspectors are valid for the currently selected
  // inspection type (by trade). Used to filter the dropdown and flag an
  // invalid selection with red styling + an inline message.
  const [selectedTypeTradeList, setSelectedTypeTradeList] = React.useState<number[]>([]);

  // The dropdown option may not carry `trades`, so fetch the full type by id
  // whenever the selection changes to reliably get its trade IDs.
  React.useEffect(() => {
    const type: any = selectedType;
    if (!type || typeof type !== 'object' || !type.id) {
      setSelectedTypeTradeList([]);
      return;
    }
    const inlineTrades = type.trades || type.tradeIds || type.trade_ids;
    if (Array.isArray(inlineTrades) && inlineTrades.length) {
      setSelectedTypeTradeList(inlineTrades.map((t: any) => Number(typeof t === 'object' ? t.id : t)));
      return;
    }
    getInspectionTypeByID(type.id)
      .then((res: any) => {
        const trades = res?.data?.trades || res?.data?.tradeIds || [];
        setSelectedTypeTradeList(trades.map((t: any) => Number(typeof t === 'object' ? t.id : t)));
      })
      .catch(() => setSelectedTypeTradeList([]));
  }, [selectedType]);

  const selectedTypeTradeIds = React.useMemo(
    () => new Set(selectedTypeTradeList),
    [selectedTypeTradeList],
  );

  const validInspectors = React.useMemo(() => {
    if (selectedTypeTradeIds.size === 0) return inspectorsData;
    return inspectorsData.filter((insp: any) => {
      const effIds = insp.effectiveTradeIds || getEffectiveTradeIds(insp?.trade, projectTypeTrades);
      return effIds.some((id: number) => selectedTypeTradeIds.has(id));
    });
  }, [inspectorsData, selectedTypeTradeIds, projectTypeTrades]);

  const isInspectorInvalid = React.useMemo(() => {
    if (!inspectorValue || !inspectorValue.id) return false;
    if (selectedTypeTradeIds.size === 0) return false;
    // Compare the CURRENT inspector's own trades against the selected type's trades.
    const inspectorTrades = inspectorValue.trade || inspectorValue.trades || [];
    const inspectorTradeIds = getEffectiveTradeIds(inspectorTrades, projectTypeTrades);
    const qualified = inspectorTradeIds.some((id: number) => selectedTypeTradeIds.has(id));
    return !qualified;
  }, [inspectorValue, selectedTypeTradeIds, projectTypeTrades]);

  React.useEffect(() => {
    const currentStatusName = selectedStatus?.name;
    const isSchedulingStatus =
      currentStatusName === InspectionStatuses.SCHEDULED ||
      currentStatusName === InspectionStatuses.NOT_SCHEDULED;

    if (!statusData.length || !isSchedulingStatus) {
      return;
    }

    const targetName = toDateValue && inspectorValue
      ? InspectionStatuses.SCHEDULED
      : InspectionStatuses.NOT_SCHEDULED;

    if (currentStatusName === targetName) {
      return;
    }

    const targetStatus = statusData.find(status => status?.name === targetName);
    if (targetStatus) {
      setValue('activityStatus', targetStatus, { shouldDirty: true });
    }
  }, [toDateValue, inspectorValue, selectedStatus, statusData]);

  React.useEffect(() => {
    if (!currentProject) {
      return;
    }
    // No jurisdiction on the project means there are no inspection types to fetch.
    if (!currentProject?.jurisdiction) {
      setInspectionTypeData([]);
      setInspectionTypesLoaded(true);
      return;
    }
    setInspectionTypesLoading(true);
    getInspectionTypesCustom(currentProject?.jurisdiction)
      .then(typesResponse => {
        setInspectionTypeData(typesResponse.data || []);
      })
      .catch(() => {
        setInspectionTypeData([]);
      })
      .finally(() => {
        setInspectionTypesLoading(false);
        setInspectionTypesLoaded(true);
      });
  }, [currentProject]);

  // Set initialValues for EditMode
  React.useEffect(() => {
    if (initialValues) {
      Promise.all([
        getActivitiesList({
          ...GET_ALL_QUERY_PARAMS,
          activityType: 8,
        }),
        getPermitUsers({
          user_role: 3,
          permit_id: permit,
          ids: initialValues?.isCompleted ? `[${initialValues.assignedToId}]` : undefined,
        }),
        getUsersCustom({
          userRole: USER_TYPES_VALUES.INSPECTOR,
          ids: initialValues?.isCompleted ? `[${initialValues.assignedToId}]` : undefined,
        }),
        getUsersCustom({
          userRole: USER_TYPES_VALUES.EMPLOYEE,
          ids: initialValues?.isCompleted ? `[${initialValues.assignedToId}]` : undefined,
        }),
        getPermitInspectionById({
          ...GET_ALL_QUERY_PARAMS,
          id: initialValues?.id,
        }),
      ]).then(
        ([
          // inspectionResponse,
          activitiesTitleList,
          allUsersList,
          inspectorsList,
          employeesList,
          inspectionIdResponse,
        ]) => {
          const projectType = inspectionIdResponse?.data?.projectType || null;
          return Promise.all([
          Promise.resolve(activitiesTitleList),
          Promise.resolve(allUsersList),
          Promise.resolve(inspectorsList),
          Promise.resolve(employeesList),
          Promise.resolve(inspectionIdResponse),
          getTradesByProjectType(projectType), // new API call
        ]);
        },
)
        .then(([activitiesTitleList, allUsersList, inspectorsList, employeesList, inspectionIdResponse, tradesResponse]) => {
          const trades = tradesResponse?.data?.trades || [];
          setProjectTypeTrades(trades);

          setInspectionTrades(inspectionIdResponse.data);

          const projectTradeIdSet = new Set(trades.map(t => t.id));

          // pick the inspection’s trade IDs (prefer initialValues.trades, fallback to API response)
          const inspectionTradeIds =
            (initialValues?.trades && initialValues.trades.length)
              ? initialValues.trades
              : (inspectionIdResponse?.data?.trades || []);

          const inspectionTradeIdSet = new Set(inspectionTradeIds.map((x: any) => Number(x)));

          // For Inspector dropdown: filter by trades
          const inspectors = (inspectorsList.data || [])
            .filter((inspector: any) => inspector?.isActive)
            .map((inspector: any) => ({
              ...inspector,
              inspectorName: `${inspector?.firstName} ${inspector?.lastName}`,
              effectiveTradeIds: getEffectiveTradeIds(inspector?.trade, trades),
            }))
            .filter((inspector: any) =>
              inspector.effectiveTradeIds.some((id: number) =>
                inspectionTradeIdSet.has(id) && projectTradeIdSet.has(id)));

          setInspectorsData(inspectors);

          // For A/V Operator dropdown: all active inspectors without trade filtering
          const allActiveInspectors = (inspectorsList.data || [])
            .filter((inspector: any) => inspector?.isActive)
            .map((inspector: any) => ({
              ...inspector,
              inspectorName: `${inspector?.firstName} ${inspector?.lastName}`,
            }));

          // Combine employees and all active inspectors for A/V Operator
          const allEmployee = employeesList.data?.filter(
            employee => employee?.isActive,
          );
          const combinedAvOperators = [...allActiveInspectors, ...(allEmployee || [])];
          setAvOperatorData(combinedAvOperators);

          const activitiesTitles = activitiesTitleList.data.results?.filter(
            title => title?.isActiveStatus === 'True',
          );
          setTitlesData(activitiesTitles);
          if (initialValues?.title || initialValues?.titleName) {
            const activityTitle = activitiesTitleList.data.results.find(
              title =>
                title.id === initialValues.title ||
                title.name === initialValues.title ||
                title.name === initialValues.titleName,
            );
            setValue('activityTitle', activityTitle);
          }

          if (initialValues?.inspectionType) {
            getInspectionTypeByID(inspectionIdResponse?.data?.inspectionType).then(typeResponse => {
              setValue('inspectionType', typeResponse?.data);
            });
          }

          if (
            initialValues?.activityContact ||
            initialValues?.contactId
          ) {
            const user = allUsersList?.data.find(el => el?.id == initialValues?.contactId);

            if (user) {
              setValue('activityContact', user);
            } else {
              getUserByID(initialValues?.contactId || "").then(userData => setValue('activityContact', userData?.data));
            }
          }

          if (initialValues?.info || initialValues?.information) {
            const info = initialValues?.info || initialValues?.information;
            setValue('information', info);
            // MUI TextareaAutosize measures its height on render/input events,
            // not when RHF sets the value imperatively — so the Information box
            // stays at its initial height and clips the notes. Nudge MUI to
            // re-measure (it listens for window 'resize') once the value is in
            // the DOM, so the field expands to show the full text.
            setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
          }

          if (initialValues?.inspector) {
            const inspector = inspectorsList.data?.find(
              el =>
                `${el?.firstName} ${el?.lastName}` ===
                  initialValues?.inspector ||
                el?.id === initialValues?.inspector,
            );

            if (inspector) {
              setValue('inspector', inspector);
            }
          }

          // Set A/V Operator following the same pattern as inspector
          // But only if clientOnSite is not checked
          if (!inspectionIdResponse?.data?.isClientonsite && (initialValues?.avOperator || inspectionIdResponse?.data?.avoperator)) {
            const avOperatorId = initialValues?.avOperator || inspectionIdResponse?.data?.avoperator;
            const avOperator = combinedAvOperators?.find(
              el =>
                `${el?.firstName} ${el?.lastName}` ===
                  avOperatorId ||
                el?.id === avOperatorId,
            );

            if (avOperator) {
              setValue('avOperator', avOperator);
            }
          }

          // setting the period data in inspection form

          if (
            initialValues
          ) {
            const period = periodData?.find(
              el =>
                el?.id === initialValues?.period ||
                el?.name === initialValues?.period,
            );

            setValue('period', period);
          }

          if (initialValues.isEmergency) {
            setValue(
              'emergency',
              initialValues.isEmergency,
            );
          }

          if (initialValues.isRequestedAtf) {
            setValue(
              'requestedatf',
              initialValues.isRequestedAtf,
            );
          }

          if (inspectionIdResponse?.data?.isClientonsite) {
            setValue(
              'clientOnSite',
              inspectionIdResponse.data.isClientonsite,
            );
          }

          if (initialValues.isCompleted) {
            setValue(
              'isCompleted',
              initialValues.isCompleted,
            );
          }
        });
    }
  }, [initialValues]);

  // Give activityStatus in action of activityTitle

  React.useEffect(() => {
    if (!selectedTitle) {
      setValue('activityStatus', '');
    }

    // `typeof null === 'object'`, and the branch above doesn't return — without the
    // truthiness check a null title falls into both branches and dies on `.id` below.
    if (selectedTitle && typeof selectedTitle === 'object') {
      setIsLoading(true);

      Promise.all([
        getActivitiesStatusList({
          ...GET_ALL_QUERY_PARAMS,
          activity: selectedTitle.id,
        }),
      ])
        .then(([activitiesStatusList]) => {
          const activitiesStatus = activitiesStatusList.data.results?.filter(
            status => status?.isActiveStatus === 'True',
          );

          setStatusData(activitiesStatus);

          // Add mode has no initialValues at all, and the request resolves after the
          // modal may have swapped them out, so every read here stays optional — a
          // missing match just leaves the status unset instead of killing the form.
          const initialStatus = activitiesStatus?.find(
            status =>
              status?.name ===
                initialValues?.activityStatus ||
              status?.name === initialValues?.status ||
              status?.id === initialValues?.status ||
              status?.name === initialValues?.statusName,
          );

          setValue('activityStatus', initialStatus);
        })
        .catch(() => {
          setValue('activityStatus', '');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [selectedTitle]);
  // /Set initialValues for EditMode

  React.useEffect(() => {
    if (
      formState.isSubmitting &&
      !Object.keys(errors).length &&
      !errorReport.length
    ) {
      setIsSubmitting(formState.isSubmitting);
    }
    if (formState.isDirty) {
      if (Object.keys(formState.dirtyFields).indexOf('activityStatus') > -1) {
        const activityStatusObject = getValues('activityStatus');
        if (activityStatusObject) {
          setSelectedStatus(activityStatusObject.name);
        }
      }
    }
  }, [formState]);

  React.useEffect(() => {
    if (Object.keys(errors).length) {
      setIsSubmitting(false);
    }
  }, [errors, initialErrors]);

  React.useEffect(() => {
    setIsLoading(true);

    Promise.all([
      getCustomActivities({
        permit_id: permit,
        ordering: null,
        event_type: "Activity",
        activity_type_name: "Conditions",
      }),
      getActivitiesList({
        ...GET_ALL_QUERY_PARAMS,
        activityType: 8,
      }),
      getUsersCustom({
        userRole: USER_TYPES_VALUES.EMPLOYEE,
        ids: initialValues?.isCompleted ? `[${initialValues.assignedToId}]` : undefined,
      }),
      getPermitByID(permit),
      getPermitUsers({
        permit_id: permit,
        userRole: USER_TYPES_VALUES.CLIENT,
        ids: initialValues?.isCompleted ? `[${initialValues.assignedToId}]` : undefined,
      }),
    ])
      .then(
        ([conditionsResponse,
          activitiesTitleList,
          employeeList,
          permitResponse,
          individualResponse,
        ]) => {
          const conditionHoldData = conditionsResponse?.data?.results?.find(el => !el?.isCompleted && (el?.activityTitleName === 'Inspection Hold' || el?.activityTitleName === 'Permit Hold'));
          if (conditionHoldData) {
            setIsConditionOnHold(true);
          } else {
            setIsConditionOnHold(false);
          }

          setPermitData(permitResponse?.data);
          Promise.all([getProjectByID(permitResponse?.data?.project)]).then(([projectResponse]) => {
            const activitiesTitles = activitiesTitleList.data.results?.filter(
              title => title?.isActiveStatus === 'True',
            );
            setTitlesData(activitiesTitles);
            const allEmployee = employeeList.data?.filter(
              employee => employee?.isActive,
            );

            setAssignedData(allEmployee);
              // setting up assigned to data for edit inspections
            if (initialValues?.assignedTo) {
              const assignee = allEmployee?.find(el =>
                `${el?.firstName} ${el?.lastName}` === initialValues?.assignedTo || el?.id === initialValues?.assignedTo);
                if (assignee) {
                setValue('assignedTo', assignee);
              }
            }

            const contacts = individualResponse?.data;
            const individualContacts = (contacts || []).filter(
              c => Number(c?.clientType) === CLIENT_TYPES_VALUES.INDIVIDUAL,
            );
            setUsersData(individualContacts);
            setAdditionalContactsData(individualContacts);
            const project = projectResponse?.data;
            setCurrentProject(project);
          });
        },
      )
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  React.useEffect(() => {
    setReadOnlyInfo([
      {
        label: 'Project Name',
        value: initialValues?.projectName || currentProject?.name,
      },
      {
        label: 'Permit Name',
        value: initialValues?.permitName,
      },
      {
        label: 'Permit Number',
        value: initialValues?.permitNumber,
      },
      {
        label: 'Permit Address',
        value: initialValues?.projectAddress ? initialValues?.projectAddress : currentProject?.address,
      },
      {
        label: 'Jurisdiction',
        value: isEmployee ? currentProject?.jurisdictionName : initialValues?.projectJurisdiction,
      },
      {
        label: 'Project Manager',
        value: isEmployee ? currentProject?.managerName : initialValues?.projectManager,
      },
      {
        label: 'Inspection Number',
        value: initialValues?.id,
      },
    ]);
  }, [currentProject]);

  React.useEffect(() => {
    if (Array.isArray(selectedType) && !initialValues) {
      Promise.all([
        getTrades({
          ...GET_ALL_QUERY_PARAMS,
          ordering: 'name',
        }),
      ]).then(([response]) => {
        const newArr = selectedType
          .map(elem => {
            return [...elem.tradeIds];
          })
          .flat();

        setTradesData(
          response.data.results.filter(elem =>
            newArr.includes(elem.id)),
        );
      });
    }
  }, [selectedType]);

  const [
    inspectionReport,
    setInspectionReport,
  ] = React.useState(null);

const downloadReport = () => {
  if (initialValues?.visibleFilename) {
    downloadInspectionReport(
      inspectionTrades.id,
    ).then(file => {
      function dataURLtoFile(dataurl, filename) {
        const arr = dataurl.split(',');
          const mime = arr[0].match(/:(.*?);/)[1];
          const bstr = atob(arr[1]);
          let n = bstr.length;
          const u8arr = new Uint8Array(n);

        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }

        return new File([u8arr], filename, {
          type: mime,
        });
      }
      const myFile = dataURLtoFile(
        `data:application/pdf;application/docx;base64,${file.data.file}`,
        file.data.fileName,
      );
        const url = window.URL.createObjectURL(myFile);
        const a = document.createElement('a');
        a.href = url;
        a.download = initialValues?.visibleFilename;
        a.click();
    }).catch(err => console.log(err));
  }
};

// ###############################

  const modifiedGetCommentsRequest = isSystemMsg => {
    return Promise.all([
      getPermitInspectionsComments(
        { ...GET_ALL_QUERY_PARAMS, system_message: isSystemMsg },
        initialValues?.id,
      ),
      getCurrentUser(),
    ]).then(([commentsResponse, userResponse]) => {
      return {
        ...commentsResponse,
        data: {
          ...commentsResponse.data,
          results: commentsResponse.data.results.reduce((acc, comment) => {
            if (comment.text !== " ") {
              const val = {
                ...comment,
              creator: `${userResponse.data.firstName} ${userResponse.data.lastName}`,
              createdAt: formatDateTime(
                comment?.createdAt,
                COMMENTS_DATETIME_FORMAT,
              ),
              };
              acc.push(val);
            }
            return acc;
          }, []),
        },
      };
    });
  };

  const middlewareAdd = (
    submitFn: (data) => void,
  ) => data => {
    if (
      Array.isArray(data.inspectionType) &&
      data.inspectionType?.length
    ) {
      data.inspectionType.forEach(insp => {
        submitFn({
          report: inspectionReport,
          permit,
          title: data.activityTitle.id,
          inspectionType: insp.id,
          information: data.information,
          cellPhone: data.cellPhone,
          phoneNumber: data.phoneNumber,
          trades: tradesData.length ? tradesData?.map(elem => elem?.id).filter(id => insp?.tradeIds?.includes(id)) : null,
          contact: data.activityContact?.id,
          isEmergency: data.emergency,
          isRequestedAtf: data.requestedatf,
          isClientOnSite: data.clientOnSite,
        });
      });
    }
  };

  const isInspectionDateDisabled = () => {
      // Scheduling an inspection does not lock the record: staff can still move the
      // date (or clear it) afterwards, so a scheduled inspection with a date on it is
      // NOT a reason to disable this field. Same while Not Scheduled — the field stays
      // interactive, and not even the permit-not-issued rule greys it out there, since
      // nothing is being scheduled yet.
      if (
        selectedStatus?.name === InspectionStatuses.NOT_SCHEDULED ||
        selectedStatus?.name === InspectionStatuses.SCHEDULED
      ) {
        return isReadOnly || isInspectionCompleted || cameFromSearch;
      }

      // Past the scheduling lifecycle (Pass, Failed, Cancelled, …) the date is history:
      // it records when the inspection actually happened, so it can no longer be
      // changed or removed.
      if (selectedStatus) {
        return true;
      }

      const isPermitNotIssued = !permitData?.permitIssued;

      const inspectionTypeValue = typeof initialValues?.inspectionType === 'string'
        ? initialValues.inspectionType
        : (initialValues as any)?.typeName || '';
      const isException = inspectionTypeValue.toLowerCase().includes('consultation');

      if (isPermitNotIssued && !isException) {
        return true;
      }

      return isReadOnly || isInspectionCompleted || cameFromSearch;
  };

  const addInspectionReport = async () => {
    if (inspectionReport) {
      const inspectordata = {
        report: inspectionReport,
        title: initialValues.title,
        inspection_type: inspectionType,
        status: selectedStatus.id,
        id: initialValues.id,
      };
      const empData = {
        report: inspectionReport,
        title: selectedTitle?.id,
        inspection_type: inspectionType,
        status: selectedStatus.id,
        id: initialValues.id,
      };

      const initialStatus = initialValues?.status;
        const status = statusData.filter(
          el => initialStatus === el.id || el.name,
        );

        const promises = [];
        if (isInspector) {
          // In `promises`, so the field save below waits for the upload and is
          // skipped when it fails. It used to run alongside it, so a failed
          // upload still saved the result with no report.
          promises.push(uploadInspectionReport(isInspector ? inspectordata : empData).then(res => {
          if (!initialValues?.isCompleted && !isInspectionCompleted) { promises.push(
          sendInspectionMessage({
          phone_number: initialValues?.cellPhone,
          inspector_id: initialValues?.inspector,
          address: initialValues?.projectAddress,
          report_status: res.data.report
            ? 'Attached'
            : 'Not Attached',
          inspection_status: selectedStatus?.name,
          inspection_id: initialValues?.id,
        }),
); }
          }));
        } else {
          promises.push(uploadInspectionReport(isInspector ? inspectordata : empData));
        }
      return Promise.all(promises).catch(err => setModalErr(true));
    }
      const initialStatus = initialValues?.status;
      const status = statusData.filter(
       el => initialStatus === el.id || el.name,
      );

      const promises = [];
      if (isInspector) {
        if (selectedStatus?.id !== initialValues?.status || inspectionReport) {
          if (!initialValues?.isCompleted && !isInspectionCompleted) { promises.push(
          sendInspectionMessage({
          phone_number: initialValues?.cellPhone,
          inspector_id: initialValues?.inspector,
          address: initialValues?.projectAddress,
          report_status: initialValues?.report
            ? 'Attached'
            : 'Not Attached',
          inspection_status: selectedStatus?.name,
          inspection_id: initialValues?.id,
        }),
); }
        }
      }

      return Promise.all([
    ]).catch(err => setModalErr(true));
  };

  React.useEffect(() => {
    if (permitStatus === 32 && initialValues?.status !== InspectionStatuses.SCHEDULED && selectedStatus?.name == InspectionStatuses.SCHEDULED) {
      setHoldModalOpen(true);
    }

    if (isConditionOnHold && initialValues?.status !== InspectionStatuses.SCHEDULED && selectedStatus?.name == InspectionStatuses.SCHEDULED) {
      setConditionHoldModalOpen(true);
    }
  }, [selectedStatus]);

  const middlewareEdit = (
    submitFn: (data) => void,
  ) => data => {
    // Every result needs its report, as in the inspection editor. This form
    // only asked for one when "Completed" was ticked, so a result could be
    // saved here with nothing attached. A report already on file counts only
    // for the status it was made with.
    //
    // "Unchanged status" must be decided by NAME, not by `selectedStatus.id ===
    // initialValues.status`. `initialValues.status` is not reliably a numeric
    // id here: depending on which page opened this form (permit page, All
    // Activities, Search, Bulk Schedule, inspector) it can be the status NAME
    // (e.g. "Pass") instead. Comparing the numeric `selectedStatus.id` to a
    // name string is always false, so the report-on-file exemption was lost and
    // editing an unrelated field (e.g. ticking Completed) on an already-passed,
    // already-reported inspection wrongly demanded a new report.
    const loadedStatusName =
      initialValues?.statusName || initialValues?.activityStatus;
    const statusUnchanged =
      selectedStatus?.name === loadedStatusName ||
      // Fall back to the id comparison for callers that pass a numeric status.
      selectedStatus?.id === initialValues?.status;
    const reportOnFile = !!(initialValues?.report || initialValues?.visibleFilename)
      && statusUnchanged;
    const needsReport = REPORT_REQUIRED_STATUSES.includes(selectedStatus?.name);
    if (needsReport && !inspectionReport && !reportOnFile) {
      setErrorReport(
        `Attach or create the inspection report before saving with status ${selectedStatus.name}.`,
      );
      setIsSubmitting(false);
      return;
    }
    if (data.isCompleted && data.activityContactCheckbox && !data.activityContact) {
      setErrorReport("Please select a contact to send the inspection report and complete the inspection");
      setIsSubmitting(false);
      return;
    }
    if (data.isCompleted && !(initialValues.visibleFilename || inspectionReport)) {
      setErrorReport("Inspection report is missing. Please attach before sending.");
      setIsSubmitting(false);
    } else {
      let completedBy;
      Promise.all([getCurrentUser()]).then(([{ data }]) => {
        completedBy = data.id;
      }).then(() => {
        const today = new Date();
        const contacts = data.additionalContacts?.map(
          contact => contact?.id,
        );

        const uploadPromise = addInspectionReport();
        uploadPromise.then(val => {
          if (val) {
            const requestData = setInspectionData(
              data,
              initialValues,
              submitFn,
              permit,
              contacts,
            );

            requestData.phone_number = data.phoneNumber;
            requestData.mobile_phone_number = data.cellPhone;
            requestData.completed_at = (initialValues && initialValues?.isCompleted === false && isInspectionCompleted === true) ? `${today.toLocaleDateString()}` : null,
            requestData.completed_by = (initialValues && initialValues?.isCompleted === false && isInspectionCompleted === true) ? completedBy : null;
            // Prompt flags are set on _data below (inspectionPrompt / conditionInspectionPrompt).
            // Setting them here too would create conflicting snake_case + camelCase keys that
            // the interceptor collapses unpredictably.
            // Clearing one of these selects has to reach the server as an explicit null.
            // An empty select gives `undefined` (or the '' default), `setInspectionData`
            // maps that straight through, and JSON drops undefined keys — the server would
            // then never see the removal and would keep the old value.
            // EA-1195: If the inspector is not qualified for the selected inspection type,
            // clear them on save so an invalid inspector is never persisted.
            const inspectorId = isInspectorInvalid ? null : (data.inspector?.id ?? null);
            const contactId = data.activityContact?.id ?? null;
            const assignedToId = data.assignedTo?.id ?? null;
            if (
              data.inspectionType &&
              !Array.isArray(data.inspectionType)
              // && !checkboxesError
            ) {
              const _data = {
                ...requestData,
                inspectionDate: requestData.inspectionDate || null,
                followUp: !requestData.inspectionDate || !data.followUp
                  ? null
                  : format(data.followUp, 'MM/dd/yyyy'),
                // The inspector stands on its own: an inspector with no date is allowed,
                // it just leaves the inspection in "Not Scheduled".
                inspector: inspectorId,
                contact: contactId,
                assignedTo: assignedToId,
                information: newInformation,
                inspectionPrompt: inspectionPrompt || holdSubmit,
                conditionInspectionPrompt: conditionPrompt || conditionHoldSubmit,
                is_requested_atf: data.requestedatf,
                isClientonsite: data.clientOnSite,
                avoperator: data.clientOnSite ? null : (data.avOperator ? data.avOperator.id : null),
              };
                submitFn(_data);
            }
            if (
              data.inspectionType &&
              Array.isArray(data.inspectionType)
              // && !checkboxesError
            ) {
              data.inspectionType.forEach(type => {
                const requestDataWithType = setInspectionData(
                  data,
                  initialValues,
                  submitFn,
                  permit,
                  contacts,
                  type,
                );
                  requestDataWithType.is_requested_atf = data.requestedatf;
                  requestDataWithType.isClientonsite = data.clientOnSite;
                  requestDataWithType.avoperator = data.clientOnSite ? null : (data.avOperator ? data.avOperator.id : null);
                  requestDataWithType.inspector = inspectorId;
                  requestDataWithType.contact = contactId;
                  requestDataWithType.assignedTo = assignedToId;
                  // setInspectionData leaves the date/follow-up keys off entirely when
                  // they are empty, and JSON drops missing keys — the server would keep
                  // the old values. Send explicit nulls so a removal actually removes.
                  if (!requestDataWithType.inspectionDate) {
                    requestDataWithType.inspectionDate = null;
                    requestDataWithType.followUp = null;
                  }
                  submitFn(requestDataWithType);
              });
            }
          }

          setIsSubmitting(false);
          dispatch(setModalOpen(false));
          const dailyInspectionUpdated = dailyInspections.reduce((itemArr, item) => {
            // @ts-ignore
            if (item.id !== initialValues.id) {
              itemArr.push(item);
            }
            return itemArr;
          }, []);

          dispatch(setPermitInspectionsList(dailyInspectionUpdated));
        }).catch(err => setModalErr(true));
      });
  }
  };

  const handleCloseContactsModal = () => {
    setIsViewContactsModal(false);
  };

  const {
    isSuperAdmin,
    isFranchiseAdmin,
    isEmployee,
    isInspector,
  } = useUserRole();

  const inspectionType = selectedType?.id || [];
  const modalOpen = useSelector((s:RootState) => s.inspections.modalOpen);
  const formRef = useRef<HTMLFormElement>();
  const handleModalOpen = () => {
    if (
      selectedStatus?.id !== initialValues?.status ||
      inspectionReport
    ) {
      dispatch(setModalOpen(true));
    }
  };

  React.useEffect(() => {
    if (modalOpen)
    {
      // iterate over the parent elements up to 10 parents.
      // if the scrollTop > 0 then reset it to zero and leave
      let parent = formRef.current.parentElement;
      let iterationCounter = 10;

      while (parent.scrollTop === 0) {
        iterationCounter--;
        parent = parent.parentElement;
        if (parent.scrollTop > 0 || iterationCounter == 0) {
          break;
        }
      }
      parent.scrollTop = 0;
    }
  }, [modalOpen]);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const handleDeleteModalOpen = () => {
    setDeleteModalOpen(true);
  };
  const closeDeleteSuccessModal = () => {
    setModalSuccess(false);
    setInspectionReport(null);
    const tempInitialValues = { ...initialValues };
    tempInitialValues.visibleFilename = null;
    setInitialValues(tempInitialValues);
    updateTable();
   };

  const handleDeleteReport = () => {
    deleteInspectionReport(initialValues.id).then(res => {
      setDeleteModalOpen(false);
      setModalSuccess(true); });
  };
  const DeleteReportButton = () => {
    return <>
      <DeleteOutline onClick={handleDeleteModalOpen} style={{ padding: 0, margin: 0, cursor: 'pointer' }} />
      {deleteModalOpen && (
      <ModalDefaultDraggable
        title="Confirmation"
        onClose={() => setDeleteModalOpen(false)}
      >
        <>
          <p>
            Are you sure you want to delete the inspection report?
          </p>
          <br />

          <ButtonWithLoader
            onClick={handleDeleteReport}
          >
            Delete
          </ButtonWithLoader>
        </>
      </ModalDefaultDraggable>
    )}
      {(modalSuccess || modalError) && (
      <ModalInfo
        timeToClose={5000}
        isError={modalError}
        onClose={closeDeleteSuccessModal}
      />
      )}
    </>;
  };

  const getButtonLabel = () => {
    if (isInspectionCompleted &&
        (additionalContacts?.length > 0 || jurisdictionCheckbox || activityContactCheckbox)) {
      return "Update & Send";
    }
    return (initialValues?.isCompleted && isInspectionCompleted) || (initialValues) ? updateButtonText : addButtonText;
  };

  const validatePhoneFields = () => {
    const isPhoneEmpty = val => !val || val.replace(/[\s-]/g, '').length === 0;
    const isPhoneValid = val => val && /^\d{3}-\d{3}-\d{4}$/.test(val.trim());
    const values = getValues();
    const newPhoneErrors: { cellPhone?: string; phoneNumber?: string } = {};
    let valid = true;

    const hasActivityContact = values.activityContact && values.activityContact.id;

    if (hasActivityContact) {
      if (isPhoneEmpty(values.cellPhone)) {
        newPhoneErrors.cellPhone = 'Cell phone is required';
        valid = false;
      } else if (!isPhoneValid(values.cellPhone)) {
        newPhoneErrors.cellPhone = 'A valid cell phone is required';
        valid = false;
      }
      if (isPhoneEmpty(values.phoneNumber)) {
        newPhoneErrors.phoneNumber = 'Phone number is required';
        valid = false;
      } else if (!isPhoneValid(values.phoneNumber)) {
        newPhoneErrors.phoneNumber = 'A valid phone number is required';
        valid = false;
      }
    } else {
      // Still validate format if a value is provided, but don't require them
      if (!isPhoneEmpty(values.cellPhone) && !isPhoneValid(values.cellPhone)) {
        newPhoneErrors.cellPhone = 'A valid cell phone is required';
        valid = false;
      }
      if (!isPhoneEmpty(values.phoneNumber) && !isPhoneValid(values.phoneNumber)) {
        newPhoneErrors.phoneNumber = 'A valid phone number is required';
        valid = false;
      }
    }
    setPhoneErrors(newPhoneErrors);
    if (!valid) return false;

    if (initialValues) {
      const currentStatus = values.activityStatus;
      const statusName = currentStatus?.name || currentStatus?.label || '';
      if (statusName.toLowerCase() === 'pending' && values.isCompleted) {
        setShowCompletedWarning(true);
        return false;
      }
    }

    return true;
  };

  // #############
  return (
    <>
      {modalError || modalErr && (
        <ModalInfo
          // timeToClose={5000}
          isError={modalErr}
          onClose={() => {
            setModalErr(false);
          }}
        />
      )}

      {showCompletedWarning && (
        <ModalDefaultDraggable
          title={<TitleWithIcon><WarningIcon style={{ color: '#f5a623', fontSize: 28 }} /> Activity completed Not Allowed</TitleWithIcon>}
          onClose={() => setShowCompletedWarning(false)}
          centered
        >
          <NotAllowedContent>
            <p>The &quot;Activity Completed&quot; field cannot be updated while the status is pending.</p>
            <Button variant="outlined" onClick={() => setShowCompletedWarning(false)}>
              CLOSE
            </Button>
          </NotAllowedContent>
        </ModalDefaultDraggable>
      )}

      <Container
        disabled={isDeleted || (isSubmitting && !modalError)}
      >
        {initialValues && (
          <FormTabs role="tablist" aria-label="Inspection sections">
            <FormTabButton
              type="button"
              role="tab"
              aria-selected={activeFormTab === 'details'}
              $active={activeFormTab === 'details'}
              onClick={() => setActiveFormTab('details')}
            >
              Details
            </FormTabButton>
            <FormTabButton
              type="button"
              role="tab"
              aria-selected={activeFormTab === 'comments'}
              $active={activeFormTab === 'comments'}
              onClick={() => setActiveFormTab('comments')}
            >
              Comments
            </FormTabButton>
          </FormTabs>
        )}

        <TabPanel hidden={initialValues && activeFormTab !== 'details'}>
        <FormContainer
          autoComplete="off"
          aria-autocomplete="none"
          ref={formRef}
          onSubmit={initialValues
            ? handleSubmit(middlewareEdit(onSubmit))
            : handleSubmit(middlewareAdd(onSubmit))
        }
        >
          {(initialValues && !(isIpad && isInspector)) ? (
            <InspectionsInfoBlock info={readOnlyInfo} />
        ) : null}
          <Section>
          <SectionEyebrow>Activity</SectionEyebrow>
            {inspectionTypesLoaded && inspectionsTypeData.length === 0 && (
              <AlertCallout>
                No inspection types are configured for this jurisdiction. Please contact
                your manager to add them in the Admin module, then reopen this form.
              </AlertCallout>
            )}
          <InlineRow>
            <InputBaseContainer>
              <Controller
                name="activityTitle"
                defaultValue=""
                control={control}
                render={props => (
                  <InputSelectWithLabel
                    placeholder=""
                    menuPlacement="bottom"
                    options={titlesData}
                    isLoading={isLoading}
                    isDisabled={isLoading || isReadOnly || isInspectionCompleted || cameFromSearch || isInspector}
                    label={labels.activityTitle}
                    error={errors.activityTitle}
                    getOptionValue={(item: any) =>
                    item.id || Math.random()
                  }
                    getOptionLabel={(item: any) => item.name}
                    isRequired
                  // eslint-disable-next-line react/jsx-props-no-spreading
                    {...props}
                  />
              )}
              />
            </InputBaseContainer>
            <InputBaseContainer>
              <Controller
                name="inspectionType"
                defaultValue=""
                control={control}
                render={props => (
                  <InputSelectWithLabel
                    isMulti={!initialValues}
                    placeholder=""
                    menuPlacement="bottom"
                    isLoading={isLoading || inspectionTypesLoading}
                    label={labels.inspectionType}
                    error={errors.inspectionType}
                    isDisabled={IsInspectionTypeDisabled()}
                    getOptionValue={(item: any) =>
                    item.id || Math.random()
                  }
                    getOptionLabel={(item: any) => item.name}
                    options={inspectionsTypeData}
                    isRequired={isYupFieldRequired(
                    'inspectionType',
                    schemaEdit,
                  )}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                    {...props}
                  />
              )}
              />
            </InputBaseContainer>
          </InlineRow>
          </Section>

          {initialValues && (
          <Section>
            <SectionEyebrow>Status &amp; Schedule</SectionEyebrow>
            <StatusContainer />
            {showFormPopup && <StatusChangeConfirmPopup setPromptValue={setPromptValue} undoStatusChange={undoStatusChange} />}
            <InputBaseContainer>
              <Controller
                name="activityStatus"
                defaultValue=""
                control={control}
                render={props => (
                  <InputSelectWithLabel
                    placeholder=""
                    menuPlacement="bottom"
                    isLoading={isLoading}
                    isDisabled={isLoading || isInspectionCompleted || cameFromSearch || isReadOnly}
                    label={labels.activityStatus}
                    error={errors.activityStatus}
                    getOptionValue={(item: any) =>
                      item.id || Math.random()
                    }
                    getOptionLabel={(item: any) =>
                      item.name
                    }
                    isRequired={isYupFieldRequired(
                      'activityStatus',
                      schemaEdit,
                    )}
                    options={statusData}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...props}
                  />
                )}
              />
            </InputBaseContainer>
            {!isInspector &&
             !permitData?.permitIssued &&
             !(
                (typeof initialValues?.inspectionType === 'string'
                  ? initialValues.inspectionType
                  : (initialValues as any)?.typeName || ''
                ).toLowerCase().includes('consultation')
             ) && (
             <AlertCallout>
               Inspection cannot be scheduled until permit is issued.
             </AlertCallout>
            )}
            <InlineRow>
              <InputBaseContainer>
                <Controller
                  name="toDate"
                  defaultValue=""
                  valueName="selected"
                  onChange={([selected]) => selected}
                  control={control}
                  render={props => (
                    <InputDatePickerWithLabel
                      label={labels.inspectionDate}
                      error={errors.inspectionDate}
                      dateFormat={DEFAULT_DATE_FORMAT}
                      disabled={isInspectionDateDisabled()}
                      isRequired={isYupFieldRequired(
                        'inspectionDate',
                        schemaEdit,
                      )}
                      // eslint-disable-next-line react/prop-types
                      selected={props.value}
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...props}
                    />
                  )}
                />
              </InputBaseContainer>
              <InputBaseContainer>
                <Controller
                  name="period"
                  defaultValue=""
                  control={control}
                  render={props => (
                    <InputSelectWithLabel
                      placeholder=""
                      menuPlacement="bottom"
                      isLoading={isLoading}
                      isDisabled={isLoading || isReadOnly || isInspectionCompleted || cameFromSearch || isInspector}
                      label={labels.period}
                      error={errors.period}
                      getOptionValue={(item: any) =>
                        item.id || Math.random()
                      }
                      getOptionLabel={(item: any) =>
                        item.name
                      }
                      options={periodData}
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...props}
                    />
                  )}
                />
              </InputBaseContainer>
            </InlineRow>
            <InlineRow>
              <InputBaseContainer hidden={isIpad && isInspector}>
                <Controller
                  name="inspector"
                  defaultValue=""
                  control={control}
                  render={props => (
                    <InputSelectWithLabel
                      placeholder=""
                      menuPlacement="bottom"
                      isLoading={isLoading}
                      isClearable
                      isDisabled={isLoading || isReadOnly || isInspectionCompleted || cameFromSearch || isInspector}
                      label={labels.inspector}
                      error={isInspectorInvalid ? { message: ' ' } : errors.inspector}
                      getOptionValue={(item: any) =>
                        item.id || Math.random()
                      }
                      getOptionLabel={(item: any) =>
                        `${item?.firstName} ${item?.lastName}`
                      }
                      options={validInspectors}
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...props}
                    />
                  )}
                />
              </InputBaseContainer>
              <InputBaseContainer hidden={isIpad && isInspector}>
                <Controller
                  name="followUp"
                  defaultValue=""
                  valueName="selected"
                  onChange={([selected]) => selected}
                  control={control}
                  render={props => (
                    <InputDatePickerWithLabel
                      label={labels.followUp}
                      error={errors.followUp}
                      dateFormat={DEFAULT_DATE_FORMAT}
                      disabled={isReadOnly || isInspectionCompleted || cameFromSearch || isInspector}
                      isRequired={isYupFieldRequired(
                        'followUp',
                        schemaEdit,
                      )}
                      // eslint-disable-next-line react/prop-types
                      selected={props.value}
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...props}
                    />
                  )}
                />
              </InputBaseContainer>
            </InlineRow>
            {isInspectorInvalid && (
              <AlertCallout>
                This inspector is not qualified for the selected inspection type. Please select a different inspector.
              </AlertCallout>
            )}
            {/* A/V Operator - only show for Remote Inspection activity title */}
            {selectedTitle?.name === 'Remote Inspection' && <>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <InputBaseContainer style={{ flex: 1 }}>
                  <Controller
                    name="avOperator"
                    defaultValue={null}
                    control={control}
                    render={props => (
                      <InputSelectWithLabel
                        placeholder=""
                        menuPlacement="bottom"
                        isLoading={isLoading}
                        isClearable
                        isDisabled={isLoading || isReadOnly || isInspectionCompleted || cameFromSearch || isInspector || clientOnSiteChecked}
                        label={labels.avOperator}
                        error={errors.avOperator}
                        getOptionValue={(item: any) =>
                          item.id || Math.random()
                        }
                        getOptionLabel={(item: any) =>
                          `${item?.firstName} ${item?.lastName}`
                        }
                        options={avOperatorData}
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...props}
                      />
                    )}
                  />
                </InputBaseContainer>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                  marginTop: '2.5rem',
                  minWidth: '200px',
                }}
                >
                  <Controller
                    name="clientOnSite"
                    control={control}
                    valueName="checked"
                    defaultValue={false}
                    render={({ onChange, onBlur, value }) => (
                      <CheckboxWithLabel
                        error={errors.clientOnSite}
                        label={labels.clientOnSite}
                        checked={value}
                        onBlur={onBlur}
                        onChange={(e, flag) => onChange(flag)}
                        disabled={isReadOnly || isInspectionCompleted || cameFromSearch || isInspector}
                      />
                    )}
                  />
                </div>
              </div>
            </>}
          </Section>
        )}
          <Section>
          <SectionEyebrow>Contact</SectionEyebrow>
          <InlineRow>
            <InputBaseContainer hidden={isIpad && isInspector}>
              <Controller
                name="activityContact"
                defaultValue=""
                control={control}
                render={({ onChange, ...props }) => (
                  <InputSelectWithLabel
                    onChange={item => {
                      onChange(item);
                      setValue('cellPhone', item?.mobilePhoneNumber || "");
                      setValue('phoneNumber', item?.phoneNumber || "");
                    }}
                    showCompany
                    placeholder=""
                    menuPlacement="bottom"
                    isClearable
                    label={labels.activityContact}
                    error={errors.activityContact}
                    dangerBackground={isContactInactive}
                    isDisabled={isLoading || isReadOnly || isInspectionCompleted || cameFromSearch || isInspector}
                    options={usersData}
                    isRequired={isYupFieldRequired(
                      'activityContact',
                      initialValues ? schemaEdit : schemaAdd,
                    )}
                    getOptionValue={(item: any) => item.id}
                    getOptionLabel={(item: any) => {
                      const name = [item?.firstName, item?.lastName].filter(Boolean).join(' ');
                      const parts = [name, item?.email, item?.companyName || item?.parentCompany?.companyName].filter(Boolean);
                      return parts.join(' - ');
                    }}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...props}
                  />
                )}
              />
            </InputBaseContainer>
            {initialValues && (
              <InputBaseContainer hidden={isIpad && isInspector}>
                <MobileRemove>
                  <Controller
                    name="assignedTo"
                    defaultValue=""
                    control={control}
                    render={props => (
                      <InputSelectWithLabel
                        placeholder=""
                        menuPlacement="bottom"
                        isLoading={isLoading}
                        isClearable
                        isDisabled={isLoading || isReadOnly || isInspectionCompleted || cameFromSearch || isInspector}
                        label={labels.assignedTo}
                        error={errors.assignedTo}
                        getOptionValue={(item: any) =>
                          item.id || Math.random()
                        }
                        getOptionLabel={(item: any) =>
                          `${item?.firstName} ${item?.lastName}`
                        }
                        options={assignedData}
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...props}
                      />
                    )}
                  />
                </MobileRemove>
              </InputBaseContainer>
            )}
          </InlineRow>
          {/* adding hold popup warning */}
          {/* {showHoldWarning && <HoldWarning />} */}
          <InlineRow>
            <InputBaseContainer>
              <Controller
                control={control}
                defaultValue=""
                name="cellPhone"
                render={({ onChange, onBlur, value }) => (
                  <ReactInputMask
                    mask="999-999-9999"
                    maskChar=" "
                    disabled={isInspector || isInspectionCompleted || cameFromSearch || isReadOnly}
                    value={value}
                    onBlur={onBlur}
                    onChange={e => {
                      onChange(e);
                      if (phoneErrors.cellPhone) setPhoneErrors(prev => ({ ...prev, cellPhone: undefined }));
                    }}
                  >
                    {() => (
                      <InputTextWithLabel
                        style={(isInspector || isInspectionCompleted || cameFromSearch || isReadOnly) ? { backgroundColor: '#F2F2F2', opacity: 0.7 } : {}}
                        error={errors.cellPhone || (phoneErrors.cellPhone ? { message: phoneErrors.cellPhone } : undefined)}
                        label={labels.cellPhone}
                        labelRequired={!!watchedActivityContact?.id}
                        isRequired={!!watchedActivityContact?.id}
                      />
                    )}
                  </ReactInputMask>
                )}
              />
            </InputBaseContainer>
            <InputBaseContainer>
              <Controller
                control={control}
                defaultValue=""
                name="phoneNumber"
                render={({ onChange, onBlur, value }) => (
                  <ReactInputMask
                    mask="999-999-9999"
                    maskChar=" "
                    disabled={isInspector || isInspectionCompleted || cameFromSearch || isReadOnly}
                    value={value}
                    onBlur={onBlur}
                    onChange={e => {
                      onChange(e);
                      if (phoneErrors.phoneNumber) setPhoneErrors(prev => ({ ...prev, phoneNumber: undefined }));
                    }}
                  >
                    {() => (
                      <InputTextWithLabel
                        style={(isInspector || isInspectionCompleted || cameFromSearch || isReadOnly) ? { backgroundColor: '#F2F2F2', opacity: 0.7 } : {}}
                        error={errors.phoneNumber || (phoneErrors.phoneNumber ? { message: phoneErrors.phoneNumber } : undefined)}
                        label={labels.phoneNumber}
                        labelRequired={!!watchedActivityContact?.id}
                        isRequired={!!watchedActivityContact?.id}
                      />
                    )}
                  </ReactInputMask>
                )}
              />
            </InputBaseContainer>
          </InlineRow>
          </Section>

          <Section>
          <SectionEyebrow>Notes</SectionEyebrow>
          {/* EA-177
        mritunjoy */}
          <InputBaseContainer>
            <TextAreaWithLabel
              name="information"
              error={errors.information}
              label={labels.information}
              inputRef={register}
              maxChar={2000}
              isRequired={isYupFieldRequired(
              'information',
              initialValues ? schemaEdit : schemaAdd,
            )}
              disabled={isInspectionCompleted || cameFromSearch}
            />
          </InputBaseContainer>
          </Section>

          {/* EA-390 */}
          {initialValues && !isInspector && (
          <Section>
            <SectionEyebrow>Audit Trail</SectionEyebrow>
            <InlineRow>
              <InputBaseContainer>
                <MobileRemove>
                  <InputTextWithLabel
                    style={{ backgroundColor: '#F2F2F2', opacity: 0.8 }}
                    name="createdAt"
                    error={errors.dateCreated}
                    inputRef={register}
                    label={labels.dateCreated}
                    isRequired={false}
                    disabled
                  />
                </MobileRemove>
              </InputBaseContainer>
              <InputBaseContainer>
                <MobileRemove>
                  <InputTextWithLabel
                    style={{ backgroundColor: '#F2F2F2', opacity: 0.8 }}
                    name="createdBy"
                    error={errors.createdBy}
                    inputRef={register}
                    label={labels.createdBy}
                    isRequired={false}
                    disabled
                  />
                </MobileRemove>
              </InputBaseContainer>
            </InlineRow>
            {initialValues?.isCompleted && (
              <InlineRow>
                <InputBaseContainer>
                  <MobileRemove>
                    <InputTextWithLabel
                      style={{ backgroundColor: '#F2F2F2', opacity: 0.8 }}
                      name="completedAt"
                      error={errors.dateCompleted}
                      inputRef={register}
                      label={labels.dateCompleted}
                      isRequired={false}
                      disabled
                    />
                  </MobileRemove>
                </InputBaseContainer>
                <InputBaseContainer>
                  <MobileRemove>
                    <InputTextWithLabel
                      style={{ backgroundColor: '#F2F2F2', opacity: 0.8 }}
                      name="completedBy"
                      error={errors.completedBy}
                      inputRef={register}
                      label={labels.completedBy}
                      isRequired={false}
                      disabled
                    />
                  </MobileRemove>
                </InputBaseContainer>
              </InlineRow>
            )}
          </Section>
          )}

          <Section>
            <SectionEyebrow>Inspection Report</SectionEyebrow>
            <div
              style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '2rem',
          }}
            >

              <div
                style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
        }}
              >
                {initialValues && (
                <Controller
                  name="isCompleted"
                  control={control}
                  valueName="checked"
                  defaultValue={false}
                  render={({ onChange, onBlur, value }) => (
                    <CheckboxWithLabel
                      error={errors.isCompleted}
                      label={labels.isCompleted}
                      checked={value}
                      onBlur={onBlur}
                      onChange={(e, flag) => onChange(flag)}
                      disabled={
                  isReadOnly ||
                  initialValues?.title === 'Invoice' ||
                  cameFromSearch ||
                  isInspector
                }
                    />
            )}
                />
        )}

                <Controller
                  name="emergency"
                  control={control}
                  valueName="checked"
                  defaultValue={false}
                  render={({ onChange, onBlur, value }) => (
                    <CheckboxWithLabel
                      error={errors.emergency}
                      label={labels.emergency}
                      checked={value}
                      onBlur={onBlur}
                      onChange={(e, flag) => onChange(flag)}
                      disabled={
                isReadOnly || isInspectionCompleted || cameFromSearch || isInspector
              }
                    />
          )}
                />

                <Controller
                  name="requestedatf"
                  control={control}
                  valueName="checked"
                  defaultValue={false}
                  render={({ onChange, onBlur, value }) => (
                    <CheckboxWithLabel
                      error={errors.requestedatf}
                      label={labels.requestedatf}
                      checked={value}
                      onBlur={onBlur}
                      onChange={(e, flag) => onChange(flag)}
                      disabled={
                isReadOnly || isInspectionCompleted || cameFromSearch || isInspector
              }
                    />
          )}
                />
              </div>

              <div
                style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '0.75rem',
        }}
              >
                {(initialValues?.visibleFilename || inspectionReport) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <a
                    onClick={() => downloadReport()}
                    style={{
            cursor: 'pointer',
            textDecoration: 'underline',
            whiteSpace: 'nowrap',
          }}
                  >
                    {inspectionReport ? inspectionReport.name : initialValues?.visibleFilename}
                  </a>

                  {initialValues?.visibleFilename &&
          !initialValues?.isCompleted &&
          !inspectionReport &&
          !cameFromSearch && <DeleteReportButton />}

                  {/* When creating new inspection, show delete button if file uploaded */}
                  {!initialValues && inspectionReport && <DeleteReportButton />}
                </div>
      )}

                {initialValues && (
                <DesktopAddReport
                  initialValues={{
              ...initialValues,
              statusName: selectedStatusValue,
              reportBase64: inspectionTrades?.reportBase64,
              isInspectionCompleted,
            }}
                  getValues={getValues}
                  cameFromSearch={cameFromSearch}
                  isReadOnly={isReadOnly}
                  setErrorReport={setErrorReport}
                  selectedStatusValue={selectedStatusValue}
                  watch={watch}
                  setInspectionReport={setInspectionReport}
                />
        )}
              </div>
            </div>

            <div style={{ color: 'red' }}>{errorReport}</div>
          </Section>
          {initialValues && (
          <Section style={{ display: isIpad && isInspector ? "none" : undefined }}>
            <SectionEyebrow>Send To</SectionEyebrow>
            <CheckboxesWrapper
              checkboxesError={checkboxesError}
            >
              <InputBaseContainer hidden={isIpad && isInspector}>
                <Controller
                  name="activityContactCheckbox"
                  control={control}
                  valueName="checked"
                  defaultValue={false}
                  render={({ onChange, onBlur, value }) => (
                    <CheckboxWithLabel
                      error={errors.activityContactCheckbox}
                      label={labels.activityContactCheckbox}
                      checked={value}
                      onBlur={onBlur}
                      onChange={(e, flag) => onChange(flag)}
                      disabled={
                        !isInspectionCompleted ||
                        initialValues?.isCompleted ||
                        cameFromSearch
                      }
                    />
                  )}
                />
                <Controller
                  name="jurisdictionContacts"
                  control={control}
                  valueName="checked"
                  defaultValue={false}
                  render={({ onChange, onBlur, value }) => (
                    <CheckboxWithLabel
                      error={errors.jurisdictionContacts}
                      label={labels.jurisdictionContacts}
                      checked={value}
                      onBlur={onBlur}
                      onChange={(e, flag) => onChange(flag)}
                      disabled={
                        !isInspectionCompleted ||
                        initialValues?.isCompleted ||
                        cameFromSearch
                      }
                    />
                  )}
                />
              </InputBaseContainer>
            </CheckboxesWrapper>
            {initialValues && (
              <InputBaseContainer hidden>
                <Controller
                  name="hiddenCheck"
                  control={control}
                  valueName="checked"
                  defaultValue
                  render={({ onChange, onBlur, value }) => (
                    <CheckboxWithLabel
                      error={errors.hiddenCheck}
                      label={labels.projectContacts}
                      checked={value}
                      onBlur={onBlur}
                      onChange={(e, flag) => onChange(flag)}
                    />
                  )}
                />
              </InputBaseContainer>
            )}

            {checkboxesError && (
              <CheckboxesError>
                {checkboxesError}
              </CheckboxesError>
            )}

            <ContactsContainer>
              <ContactsInputWrapper>
                <Controller
                  name="additionalContacts"
                  defaultValue=""
                  control={control}
                  render={props => (
                    <InputSelectWithLabel
                      isMulti
                      // selectAllActive
                      placeholder=""
                      menuPlacement="top"
                      isLoading={isLoading}
                      label={labels.additionalContacts}
                      error={errors.additionalContacts}
                      isDisabled={
                        isLoading ||
                        !isInspectionCompleted ||
                        initialValues?.isCompleted ||
                        cameFromSearch
                      }
                      getOptionValue={(item: any) =>
                        item.id || Math.random()
                      }
                      getOptionLabel={(item: any) =>
                        `${item.firstName} ${item.lastName}${
                          item?.email ? ` - ${item?.email}` : ''
                        }${
                          item.companyName
                            ? ` - ${item.companyName}`
                            : ''
                        }`
                      }
                      options={additionalContactsData}
                      isRequired={isYupFieldRequired(
                        'additionalContacts',
                        schemaEdit,
                      )}
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...props}
                    />
                  )}
                />
              </ContactsInputWrapper>
              <ContactsButtonWrapper>
                <ButtonWithLoader
                  disabled={
                    isLoading ||
                    !isInspectionCompleted ||
                    initialValues?.isCompleted ||
                    cameFromSearch
                  }
                  loading={isLoading}
                  onClick={() =>
                    setIsViewContactsModal(true)
                  }
                >
                  View Contacts
                </ButtonWithLoader>
              </ContactsButtonWrapper>
            </ContactsContainer>
          </Section>
        )}
          {/* adding onclick event to upload inspection report
                   mritunjoy
                   EA-54 */}
          {isInspector ? (
            <>
              <ButtonContainer>
                {!isDeleted && (
                <ButtonWithLoader
                  onClick={e => { if (!validatePhoneFields()) { e.preventDefault(); return; } handleModalOpen(); }}
                  disabled={isSubmitting && !modalError}
                  loading={isSubmitting && !modalError}
                  type={(selectedStatus?.id === initialValues?.status && !inspectionReport) ? 'submit' : 'button'}
                >
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {getButtonLabel()}
                </ButtonWithLoader>
              )}
              </ButtonContainer>
              {modalOpen && (
              <ModalDefaultDraggable
                title="Confirmation"
                onClose={() => dispatch(setModalOpen(false))}
              >
                <>
                  <p>
                    This will send inspection completed
                    message to the inspection contact. Do
                    you want to continue?
                  </p>
                  <br />
                  <Button
                    onClick={() => dispatch(setModalOpen(false))}
                  >
                    Cancel
                  </Button>{' '}
                  <ButtonWithLoader
                    disabled={isSubmitting && !modalError}
                    loading={isSubmitting}
                    type="submit"
                  >
                    Ok
                  </ButtonWithLoader>
                </>
              </ModalDefaultDraggable>
            )}
            </>
        ) : isEmployee ? (
          <ButtonContainer>
            {!isDeleted && !isReadOnly && (
              <ButtonWithLoader
                onClick={e => { if (!validatePhoneFields()) e.preventDefault(); }}
                disabled={isSubmitting && !modalError || cameFromSearch}
                loading={isSubmitting && !modalError}
                type="submit"
              >
                {/* eslint-disable-next-line no-nested-ternary */}
                {getButtonLabel()}
              </ButtonWithLoader>
            )}
          </ButtonContainer>
        ) : null}

          {holdModalOpen && (
          <ModalDefaultDraggable
            title="Confirmation"
            onClose={() => setHoldModalOpen(false)}
          >
            <>
              <p>
                Permit Status is HOLD. Are you sure you want to schedule the inspection
              </p>
              <br />
              <Button
                onClick={() => {
                      setHoldModalOpen(false);
                      setHoldSubmit(false);
                      setValue('activityStatus', statusData.find(status => status?.name === initialValues?.status));
                    }}
              >
                No
              </Button>{' '}
              <ButtonWithLoader
                disabled={isSubmitting && !modalError}
                loading={isSubmitting && !modalError}
                onClick={() => {
                    setHoldSubmit(true);
                    setHoldModalOpen(false);
                  }}
              >
                Yes
              </ButtonWithLoader>
            </>
          </ModalDefaultDraggable>
            )}
          {/* EA-557 adding confirmation popup for hold permit */}
          {false && (
          <ModalDefaultDraggable
            title="Confirmation"
            onClose={() => setConditionHoldModalOpen(false)}
          >
            <>
              <p>
                There is a condition with either an Inspection Hold or Permit Hold. Are you sure you want to schedule the inspection?
              </p>
              <br />
              <Button
                onClick={() => {
                      setConditionHoldModalOpen(false);
                      setConditionHoldSubmit(false);
                      setValue('activityStatus', statusData.find(status => status?.name === initialValues?.status));
                    }}
              >
                No
              </Button>{' '}
              <ButtonWithLoader
                disabled={isSubmitting && !modalError}
                loading={isSubmitting && !modalError}
                onClick={() => {
                    setConditionHoldSubmit(true);
                    setConditionHoldModalOpen(false);
                  }}
              >
                Yes
              </ButtonWithLoader>
            </>
          </ModalDefaultDraggable>
            )}
        </FormContainer>
        </TabPanel>

        {initialValues && (
        <TabPanel hidden={activeFormTab !== 'comments'}>
          <CommentsChat
            commentsRequest={modifiedGetCommentsRequest}
            entityData={initialValues}
            postRequest={createPermitInspectionsComment}
            noFile
            title="Comments List"
            buttonText="Add Comment"
            isButtonHidden={isSimulateDeleted}
            isReadOnly={initialValues?.isCompleted || cameFromSearch || isReadOnly}
            commentCreated={commentCreated}
            setCommentCreated={setCommentCreated}
          />
        </TabPanel>
      )}

        {isViewContactsModal && (
        <StyledModalDraggable
          onClose={handleCloseContactsModal}
          title="Contacts"
          centered
          wide
        >
          <PermitViewContacts
            permit={permit}
            setContactUpdated={() => {}}
            noEdit
          />
        </StyledModalDraggable>
      )}
      </Container>
    </>
  );
};

const StatusContainer = styled.div`
 position: relative;;
`;
const Container = styled.fieldset`
  display: block;
  margin: 0;
  padding: 0;
  width: 100%;
  min-width: 0;
  border: none;
  text-align: left;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif;
  font-size: 0.875rem;
  color: var(--mainText);
  box-sizing: border-box;
`;

const FormTabs = styled.div`
  display: flex;
  gap: 4px;
  background: var(--neutral-100);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 4px;
  margin: 0 0 20px;
  width: 100%;
  box-sizing: border-box;
`;

const FormTabButton = styled.button<{ $active: boolean }>`
  flex: 1;
  height: 40px;
  border-radius: 8px;
  border: none;
  font-family: inherit;
  font-weight: 600;
  font-size: 0.9375rem;
  letter-spacing: 0.01em;
  text-transform: none;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  background: ${({ $active }) => ($active ? 'var(--primary-600)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--neutral-0)' : 'var(--neutral-600)')};

  &:hover {
    color: ${({ $active }) => ($active ? 'var(--neutral-0)' : 'var(--mainText)')};
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const TabPanel = styled.div`
  width: 100%;
  &[hidden] {
    display: none;
  }
`;

const FormContainer = styled.form`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;

  /* Compact vertical rhythm for the Edit Inspection modal —
     halves the gap between InputBaseContainer rows. */
  --input-row-padding-y: 3px;

  h2 {
    font-size: 1rem;
    font-weight: 700;
    color: var(--mainText);
    margin: 12px 0 8px;
  }

  /* Normalize MUI buttons rendered inside the form: no uppercase,
     modern radius/weight. Keeps field-component internals intact. */
  .MuiButton-root {
    text-transform: none;
    letter-spacing: 0.01em;
    font-weight: 600;
    border-radius: 8px;
  }
`;

const InlineRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  width: 100%;

  /* Neutralize InputBaseContainer's calc+negative-margin shim
     when used as a flex sibling, otherwise children overlap. */
  & > * {
    flex: 1 1 0;
    min-width: 0;
    width: 100%;
    margin: 0;
  }
  & > * > div {
    width: 100%;
    margin: 0;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 0;
  }
`;

const Section = styled.section`
  padding: 14px 16px 12px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-50);
  box-sizing: border-box;
  margin: 0 0 12px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const SectionEyebrow = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
  margin: 0 0 8px;
`;

const AlertCallout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: var(--danger-50, #fef2f2);
  border: 1px solid var(--danger-100, #fee2e2);
  color: var(--danger-700, #b91c1c);
  border-radius: 8px;
  padding: 8px 12px;
  margin: 0 0 6px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.4;

  &::before {
    content: '!';
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    border-radius: 999px;
    background: var(--danger-500, #ef4444);
    color: var(--neutral-0, #fff);
    font-size: 0.6875rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    margin-top: 1px;
  }
`;

const ContactsContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 8px 8px;

  & button {
    margin-top: 0 !important;
  }
`;

const ContactsInputWrapper = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
`;

const ContactsButtonWrapper = styled.div`
  flex: 0 0 auto;
  margin-top: 0;
  padding-bottom: 4px;

  .MuiButton-root {
    min-height: 40px;
    padding: 0 16px;
    white-space: nowrap;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--border);

  .MuiButton-root {
    min-height: 40px;
    min-width: 140px;
    padding: 0 20px;
    margin-top: 0 !important;
    text-transform: none;
    letter-spacing: 0.01em;
    font-weight: 600;
    font-size: 0.875rem;
    border-radius: 8px;
  }
`;

const CheckboxesError = styled.p`
  color: var(--danger-500);
  font-size: 0.75rem;
  margin: 3px 14px 0px;
  text-align: left;
`;

const CheckboxesWrapper = styled.div<Error>`
  ${({ checkboxesError }) =>
    checkboxesError
      ? 'border: 1px solid var(--danger-500); border-radius: 8px; padding: 4px;'
      : ''}
`;

const StyledModalDraggable = styled(ModalDefaultDraggable)`
  height: 100%;
  overflow: inherit;
`;

const MobileRemove = styled.div`
  @media only screen and (max-width: 840px) {
    display: none;
  }
`;

const TitleWithIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 700;
`;

const NotAllowedContent = styled.div`
  text-align: center;
  p {
    margin: 16px 0 24px;
    font-size: 0.875rem;
    color: var(--mainText);
  }
  .MuiButton-root {
    text-transform: none;
    letter-spacing: 0.01em;
    font-weight: 600;
    border-radius: 8px;
    min-height: 40px;
    min-width: 120px;
  }
`;

interface Props {
  addButtonText?: string;
  updateButtonText?: string;
  initialValues?: any;
  allActivities?: boolean;
  onSubmit: (data) => void;
  initialErrors?: FormServerError<any>;
  modalError: boolean;
  permit: number;
  isReadOnly?: boolean;
  isSimulateDeleted?: boolean;
  cameFromSearch?:boolean;
}

interface Error {
  checkboxesError: string;
}

export default PermitInspectionsForm;
